import React, { useEffect, useState } from "react";
import { Button, Field, TextInput, Select, Loader } from "../../../../../ui";
import { Dialog } from "../ui-pending";
import { toast } from "react-toastify";
import {
  studentCourses,
  getGradedExamList,
  confirmGradedExamUserPayment,
} from "../../../../../Api/apiUtils";

interface ExternalExamProps {
  isOpen: boolean;
  toggle: () => void;
  studentId: string;
  profileData: any;
}

const ALLOWED_EXAM_STATUSES = new Set([1, 2, 3]);

const ExternalExam: React.FC<ExternalExamProps> = ({
  isOpen,
  toggle,
  studentId,
  profileData,
}) => {
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingExams, setLoadingExams] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paidCourses, setPaidCourses] = useState<any[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [teacherName, setTeacherName] = useState<string>("");
  const [exams, setExams] = useState<any[]>([]);
  const [selectedExamId, setSelectedExamId] = useState<string>("");
  const [orderId, setOrderId] = useState<string>("");

  const resetForm = () => {
    setSelectedCourseId("");
    setTeacherName("");
    setExams([]);
    setSelectedExamId("");
    setOrderId("");
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  useEffect(() => {
    if (!isOpen) return;

    const loadPaidCourses = async () => {
      setLoadingCourses(true);
      resetForm();
      try {
        const courses = await studentCourses(studentId);
        const paid = (Array.isArray(courses) ? courses : []).filter(
          (c: any) => c && c.isDemoCourse === false
        );
        setPaidCourses(paid);
      } catch (error) {
        console.error("Error loading paid courses:", error);
        toast.error("Failed to load paid courses");
        setPaidCourses([]);
      } finally {
        setLoadingCourses(false);
      }
    };

    loadPaidCourses();
  }, [isOpen, studentId]);

  const resolveUserId = (course: any) =>
    course?.studentUserId ||
    course?.userId ||
    profileData?.userId ||
    localStorage.getItem("studentUserId") ||
    "";

  const getCurrency = (exam?: any) => {
    if (exam?.currencyCode) return exam.currencyCode;
    const tz = String(profileData?.timezone || "").toLowerCase();
    if (tz === "asia/kolkata" || tz === "asia/calcutta") return "INR";
    return "USD";
  };

  const getStudentName = () =>
    [profileData?.firstName, profileData?.lastName].filter(Boolean).join(" ").trim() ||
    profileData?.studentName ||
    "";

  const handleCourseChange = async (courseId: string) => {
    setSelectedCourseId(courseId);
    setSelectedExamId("");
    setExams([]);

    const course = paidCourses.find(
      (c: any) => String(c.courseId) === String(courseId)
    );
    setTeacherName(course?.teacherName || "");

    if (!courseId) return;

    const userId = resolveUserId(course);
    if (!userId) {
      toast.error("Student userId not found for exam list");
      return;
    }

    setLoadingExams(true);
    try {
      const response = await getGradedExamList(courseId, userId);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      const filtered = list.filter((exam: any) =>
        ALLOWED_EXAM_STATUSES.has(Number(exam?.status))
      );
      setExams(filtered);
      if (!filtered.length) {
        toast.info("No exams found with status 1, 2, or 3 for this course");
      }
    } catch (error) {
      console.error("Error loading exams:", error);
      toast.error("Failed to load exam list");
      setExams([]);
    } finally {
      setLoadingExams(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCourseId) {
      toast.error("Please select a course");
      return;
    }
    if (!selectedExamId) {
      toast.error("Please select an exam");
      return;
    }
    if (!orderId.trim()) {
      toast.error("Order Id is mandatory");
      return;
    }
    if (submitting) return;

    const course = paidCourses.find(
      (c: any) => String(c.courseId) === String(selectedCourseId)
    );
    const exam = exams.find(
      (e: any) => String(e.exam_id) === String(selectedExamId)
    );
    if (!course || !exam) {
      toast.error("Selected course or exam not found");
      return;
    }

    const userId = resolveUserId(course);
    if (!userId) {
      toast.error("Student userId not found");
      return;
    }

    const amountRaw = exam.discounted_exam_fee ?? exam.exam_fee ?? 0;
    const amount = Number(amountRaw);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Valid exam amount not found");
      return;
    }

    const payload = {
      examId: String(exam.exam_id),
      userId: String(userId),
      orderId: orderId.trim(),
      amount,
      currency: getCurrency(exam),
      name: getStudentName(),
      examName: exam.exam_name || "",
      courseName: exam.course_name || course.courseName || "",
      lessonId: String(exam.lesson_id ?? ""),
      courseId: Number(course.courseId),
      lessonNumber: Number(exam.lesson_number ?? 0),
      levelName: exam.level_name || "",
      teacherName: course.teacherName || exam.teacher_name || teacherName || "",
      teacherUserId: String(
        course.teacherUserId || course.teacherId || exam.teacher_id || ""
      ),
      teacherEmail: String(course.teacherEmail || exam.user_email || ""),
      ageGroup: profileData?.ageGroup || "",
      contact: profileData?.mobileNumber || "",
      country:
        profileData?.city || "",
      gender: profileData?.gender || "",
      remainingClasses: Number(course?.remainingClasses ?? 0),
      membershipType: profileData?.membershipType || "",
    };

    setSubmitting(true);
    try {
      await confirmGradedExamUserPayment(payload);
      toast.success("Student added to dashboard successfully");
      handleClose();
    } catch (error: any) {
      console.error("Error confirming exam payment:", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to add student to dashboard";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Add external exam"
      actions={
        <>
          {/* <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Close
          </Button> */}
          <Button variant="primary" onClick={handleSubmit} loading={submitting} disabled={loadingCourses}>
            {submitting ? "Adding..." : "Add Student to Dashboard"}
          </Button>
        </>
      }
    >
      {loadingCourses ? (
        <Loader label="Loading courses…" />
      ) : (
        <>
          <Field
            id="externalExamCourse"
            label="Course"
            hint={!paidCourses.length ? "No paid courses found for this student." : undefined}
          >
            {(ctl) => (
              <Select
                {...ctl}
                value={selectedCourseId}
                onChange={(e) => handleCourseChange(e.target.value)}
                options={[
                  { value: "", label: "Select paid course" },
                  ...paidCourses.map((course: any) => ({
                    value: String(course.courseId),
                    label: `${course.courseName} - Paid`,
                  })),
                ]}
              />
            )}
          </Field>

          <Field id="externalExamTeacher" label="Teacher Name">
            {(ctl) => (
              <TextInput
                {...ctl}
                type="text"
                value={teacherName}
                readOnly
                placeholder="Select a course to see teacher"
              />
            )}
          </Field>

          {loadingExams ? (
            <Loader label="Loading exams…" />
          ) : (
            <Field id="externalExamName" label="Exam Name">
              {(ctl) => (
                <Select
                  {...ctl}
                  value={selectedExamId}
                  onChange={(e) => setSelectedExamId(e.target.value)}
                  disabled={!selectedCourseId}
                  options={[
                    { value: "", label: "Select exam" },
                    ...exams.map((exam: any) => ({
                      value: String(exam.exam_id),
                      label: `${exam.exam_name}${exam.level_name ? ` (${exam.level_name})` : ""}`,
                    })),
                  ]}
                />
              )}
            </Field>
          )}

          <Field id="externalExamOrderId" label="Payment Id" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Enter order id"
                required
              />
            )}
          </Field>
        </>
      )}
    </Dialog>
  );
};

export default ExternalExam;
